<template>
  <div class="compare-page">
    <div class="container py-4">
      <div class="d-flex justify-content-end mb-2">
        <button
          v-if="hasProducts"
          class="btn compare-clear-btn"
          @click="clearAll"
        >
          <i class="bi bi-trash3 me-1"></i> Clear
        </button>
      </div>
      <div class="text-center mb-4">
        <h1 class="compare-heading">Compare Products</h1>
        <p class="compare-subheading">
          Search a product on each side and compare them feature by feature.
        </p>
      </div>

      <!-- Product headers: one per side. Shows a search box when empty,
           or the selected product (image, title, price, actions) when filled. -->
      <div class="compare-grid compare-header">
        <div
          v-for="(slot, index) in slots"
          :key="`head-${index}`"
          class="compare-column"
        >
          <!-- Selected product -->
          <div v-if="slot.product" class="selected-product">
            <button
              class="remove-btn"
              title="Remove"
              @click="removeSide(index)"
            >
              <i class="bi bi-x-lg"></i>
            </button>
            <router-link
              :to="'/product/' + slot.product._id + `/${slugify(slot.product.title)}`"
            >
              <img
                :src="imageOf(slot.product)"
                alt="Product image"
                class="selected-image"
              />
            </router-link>
            <router-link
              class="selected-title"
              :to="'/product/' + slot.product._id + `/${slugify(slot.product.title)}`"
            >
              {{ slot.product.title }}
            </router-link>
            <div class="selected-price" v-if="slot.product.minVariant">
              <span v-if="slot.product.minVariant.salePrice">
                <span class="text-decoration-line-through text-muted me-1">
                  ৳ {{ slot.product.minVariant.price }}
                </span>
                <span>৳ {{ slot.product.minVariant.salePrice }}</span>
              </span>
              <span v-else>৳ {{ slot.product.minVariant.price }}</span>
            </div>
            <button
              class="btn btn-sm btn-add-cart mt-2"
              @click="addToCart(slot.product)"
            >
              <i class="bi bi-cart3"></i> Add to cart
            </button>
          </div>

          <!-- Empty slot: search box -->
          <div v-else class="search-slot">
            <div v-if="slot.loading" class="text-center py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <template v-else>
              <div class="empty-illustration">
                <i class="bi bi-plus-circle"></i>
                <p>Search a product to compare</p>
              </div>
              <div class="search-box">
                <input
                  v-model="slot.query"
                  type="text"
                  class="search-input"
                  :placeholder="index === 0 ? 'Search left product...' : 'Search right product...'"
                  @input="debounceSearch(index)"
                  @focus="slot.showSuggestions = true"
                  @blur="hideSuggestions(index)"
                />
                <i class="bi bi-search search-icon"></i>
                <div class="search-result-wrapper">
                  <ul
                    v-if="slot.suggestions.length && slot.query && slot.query.trim() && slot.showSuggestions"
                    class="search-dropdown"
                  >
                    <li
                      v-for="(product, s) in slot.suggestions"
                      :key="`sugg-${index}-${s}`"
                      @mousedown.prevent="selectProduct(index, product)"
                    >
                      <img
                        :src="imageOf(product)"
                        class="suggestion-product-image"
                        alt="Product image"
                      />
                      <span>{{ product.title }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Comparison table (Apple-style, row-aligned). Only rendered once at
           least one side has a product selected. -->
      <div v-if="leftProduct || rightProduct" class="compare-body">
        <!-- Price row (custom markup for strikethrough) -->
        <div class="compare-row">
          <div class="compare-label">Price</div>
          <div class="compare-grid compare-values">
            <div
              v-for="(slot, index) in slots"
              :key="`price-${index}`"
              class="compare-cell"
            >
              <span v-if="slot.product && slot.product.minVariant">
                <span v-if="slot.product.minVariant.salePrice">
                  <span class="text-decoration-line-through text-muted me-1">
                    ৳ {{ slot.product.minVariant.price }}
                  </span>
                  <span>৳ {{ slot.product.minVariant.salePrice }}</span>
                </span>
                <span v-else>৳ {{ slot.product.minVariant.price }}</span>
              </span>
              <span v-else class="text-muted">—</span>
            </div>
          </div>
        </div>

        <!-- Generic rows -->
        <div
          v-for="row in rows"
          :key="row.key"
          class="compare-row"
        >
          <div class="compare-label">{{ row.label }}</div>
          <div class="compare-grid compare-values">
            <div
              v-for="(slot, index) in slots"
              :key="`${row.key}-${index}`"
              class="compare-cell"
            >
              {{ displayValue(slot.product, row.key) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { BASE_URL, BASE_IMAGE_PREFIX, PLACEHOLDER_IMAGE } from "@/globals";
import { analytics } from "@/services/analytics";

export default {
  name: "ComparePage",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      PLACEHOLDER_IMAGE,
      // index 0 = left, index 1 = right
      slots: [
        {
          query: "",
          suggestions: [],
          showSuggestions: false,
          searchTimeout: null,
          loading: false,
          product: null,
        },
        {
          query: "",
          suggestions: [],
          showSuggestions: false,
          searchTimeout: null,
          loading: false,
          product: null,
        },
      ],
      // Attribute rows rendered in the comparison table. The same key sits on
      // the same row for both sides.
      rows: [
        { key: "brand", label: "Brand" },
        { key: "variants", label: "Variants" },
        // { key: "availability", label: "Availability" },
        { key: "category", label: "Categories" },
        { key: "skinTypes", label: "Skin Types" },
        { key: "concerns", label: "Concerns" },
        { key: "ingredients", label: "Ingredients" },
        { key: "countryOfOrigin", label: "Country of Origin" },
        { key: "warning", label: "Warning" },
        { key: "description", label: "Description" },
      ],
    };
  },
  computed: {
    leftProduct() {
      return this.slots[0].product;
    },
    rightProduct() {
      return this.slots[1].product;
    },
    hasProducts() {
      return !!(this.slots[0].product || this.slots[1].product);
    },
  },
  mounted() {
    this.loadFromStore();
  },
  methods: {
    // On mount, read the product ids saved in the global compare state and
    // fetch their full data so the comparison shows automatically.
    async loadFromStore() {
      const compareIds = this.$store.getters.compareItems || [];
      await Promise.all(
        compareIds.slice(0, 2).map((id, index) =>
          this.fetchIntoSlot(index, id)
        )
      );
    },
    async fetchIntoSlot(index, id) {
      this.slots[index].loading = true;
      try {
        const response = await axios.get(`${BASE_URL}/api/products/${id}`);
        this.slots[index].product = response.data;
      } catch (error) {
        console.error("Error fetching product for comparison:", error);
        // Drop stale/invalid ids from the store so they don't linger.
        this.$store.dispatch("removeFromCompare", id);
      } finally {
        this.slots[index].loading = false;
      }
    },
    debounceSearch(index) {
      const slot = this.slots[index];
      clearTimeout(slot.searchTimeout);
      if (slot.query && slot.query.trim()) {
        slot.searchTimeout = setTimeout(() => {
          this.fetchSuggestions(index);
        }, 500);
      } else {
        slot.suggestions = [];
      }
    },
    async fetchSuggestions(index) {
      const slot = this.slots[index];
      try {
        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            q: slot.query?.trim() || undefined,
            limit: 5,
            hasActiveVariant: true,
          },
        });
        slot.suggestions = response.data.products;
        slot.showSuggestions = true;
        analytics.search(slot.query);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    },
    async selectProduct(index, product) {
      const otherIndex = index === 0 ? 1 : 0;
      // Prevent comparing the same product against itself.
      if (this.slots[otherIndex].product?._id === product._id) {
        this.$root.showToast({
          message: "This product is already on the other side",
          status: "error",
          duration: 3000,
        });
        return;
      }

      const slot = this.slots[index];
      slot.suggestions = [];
      slot.query = "";
      slot.showSuggestions = false;

      // Fetch full details (list results may omit some attributes) and persist
      // the id to the global compare state.
      await this.fetchIntoSlot(index, product._id);
      this.$store.dispatch("addToCompare", product._id);
    },
    removeSide(index) {
      const slot = this.slots[index];
      if (slot.product) {
        this.$store.dispatch("removeFromCompare", slot.product._id);
      }
      slot.product = null;
      slot.query = "";
      slot.suggestions = [];
    },
    clearAll() {
      this.$store.dispatch("clearCompare");
      this.slots.forEach((slot) => {
        slot.product = null;
        slot.query = "";
        slot.suggestions = [];
        slot.showSuggestions = false;
      });
    },
    hideSuggestions(index) {
      // Delay so the mousedown selection can register first.
      setTimeout(() => {
        this.slots[index].showSuggestions = false;
      }, 150);
    },
    displayValue(product, key) {
      if (!product) return "";
      let value;
      switch (key) {
        case "brand":
          value = product.brand;
          break;
        case "variants":
          value = product.variants?.map((v) => v.name).join(", ");
          break;
        // case "availability":
        //   return product.stock > 0 ? "In stock" : "Out of stock";
        case "category":
          value = product.category
            ?.map((c) => c.replace(/_/g, " "))
            .join(", ");
          break;
        case "skinTypes":
          value = product.skinTypes?.join(", ");
          break;
        case "concerns":
          value = product.concerns?.join(", ");
          break;
        default:
          value = product[key];
      }
      return value && String(value).trim() ? value : "—";
    },
    imageOf(product) {
      return product?.images?.length
        ? `${BASE_IMAGE_PREFIX}/${product.images[0]}`
        : this.PLACEHOLDER_IMAGE;
    },
    addToCart(product) {
      const cartItem = {
        product,
        quantity: 1,
        variant: product.minVariant,
      };
      this.$store.dispatch("addToCart", cartItem);
      this.$root.showToast({
        message: "Item added to cart",
        status: "success",
        duration: 3000,
      });
      if (product.minVariant) {
        analytics.addToCart(
          product,
          product.minVariant.salePrice || product.minVariant.price,
          1
        );
      }
    },
    slugify(text) {
      return text
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },
  },
};
</script>

<style lang="scss" scoped>
.compare-page {
  min-height: 60vh;
}

.compare-heading {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
}

.compare-subheading {
  color: var(--text-gray, #6b7280);
  font-size: 15px;
}

.compare-clear-btn {
  background-color: #111827;
  color: #fff;
  border: 1px solid #111827;
  font-size: 14px;
  padding: 6px 18px;
  border-radius: 6px;

  &:hover {
    background-color: #000;
    color: #fff;
  }
}

/* Two-column grid shared by the product headers and every comparison row so
   the left/right values always line up. */
.compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.compare-header {
  margin-bottom: 24px;
}

.compare-column {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 20px;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Selected product card */
.selected-product {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .remove-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #e11d48;
      color: #fff;
    }
  }

  .selected-image {
    width: auto;
    max-height: 160px;
    object-fit: contain;
    border-radius: 12px;
    margin-bottom: 12px;
  }

  .selected-title {
    font-weight: 600;
    color: #111827;
    text-decoration: none;
    margin-bottom: 6px;

    &:hover {
      color: var(--primary-color);
    }
  }

  .selected-price {
    color: var(--primary-color);
    font-weight: 700;
    font-size: 18px;
  }

  .btn-add-cart {
    background-color: var(--secondary-color);
    color: #fff;
    border: none;
    border-radius: 6px;

    &:hover {
      opacity: 0.9;
    }
  }
}

/* Empty search slot */
.search-slot {
  width: 100%;

  .empty-illustration {
    color: #9ca3af;
    margin-bottom: 16px;

    i {
      font-size: 36px;
      display: block;
      margin-bottom: 6px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}

.search-box {
  position: relative;

  .search-input {
    border-radius: 8px;
    padding: 12px 40px 12px 16px;
    border: 1px solid #e2e2e2;
    width: 100%;
    background-color: #fff;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
    }
  }

  .search-icon {
    position: absolute;
    top: 14px;
    right: 15px;
    color: #888;
  }
}

.search-result-wrapper {
  position: relative;

  .search-dropdown {
    position: absolute;
    width: 100%;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 10px;
    max-height: 240px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 5px 0 0 0;
    z-index: 5;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-align: left;

    li {
      padding: 10px;
      cursor: pointer;
      display: flex;
      align-items: center;

      .suggestion-product-image {
        width: auto;
        height: 40px;
        border-radius: 5px;
        margin-right: 10px;
        object-fit: cover;
      }

      &:hover {
        background: #f0f0f0;
      }
    }
  }
}

/* Comparison rows */
.compare-body {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  overflow: hidden;
}

.compare-row {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:nth-child(even) {
    background: #fafafa;
  }

  &:last-child {
    border-bottom: none;
  }

  .compare-label {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    margin-bottom: 8px;
  }

  .compare-cell {
    color: #111827;
    font-size: 15px;
    line-height: 1.5;
    word-break: break-word;
    text-transform: capitalize;

    &:first-child {
      padding-right: 8px;
    }
  }
}

@media (max-width: 575.98px) {
  .compare-column {
    padding: 14px;
    min-height: 220px;
  }

  .compare-heading {
    font-size: 24px;
  }

  .compare-row .compare-cell {
    font-size: 13px;
  }
}
</style>
